import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import { CovertAndFormatCentsToDollarsWithDollarSign } from "src/common/Methods/FileUtil";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { HorseForSaleEndpoints } from "src/endpoints/HorseForSaleEndpoints";
import { CreditApplicationState, Customer, ExtractFriendlyName, PermanentCreditApplication, OnTheSpotCreditApplication } from "src/model/Customer";
import { Invoice } from "src/model/Financial";
import { HammerFallOutcome, HorseForSale, SimpleHorseSale, HorseSale } from "src/model/HorseForSale";
import { CustomerStore } from "src/stores/CustomerStore";

import {
    faCashRegister,
    faCheckCircle,
    faDollarSign,
    faDownload,
    faExclamationCircle,
    faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as fileUtil from "../../common/Methods/FileUtil";
import { CustomerLink } from "../common/CustomerLink";
import { FormDialog } from "../common/FormDialog";
import { errorMessage } from "../common/Toast";
import SettlementDialog from "../customer/SettlementDialog";
import { CheckoutTabs, EditableHorseForSalePurchase } from "./CheckoutTemplate";
import { CustomerSortFn, SortFn } from "./CommonTableSorting";
import { CustomerLinkCheckout } from "../common/CustomerLinkCheckout";
import { ConfigurationStore } from "src/stores/ConfigurationStore";

interface CustomerStoreHackMapType {
    [customerId: number]: CustomerStore;
}

const CustomerStoreHackMap: CustomerStoreHackMapType = {};
const SetUpCustomerStore = (customerEndpoints: CustomerEndpoints, customer: Customer) => {
    if (!CustomerStoreHackMap[customer.id]) {
        CustomerStoreHackMap[customer.id] = new CustomerStore(customerEndpoints, customer);
    } else {
        CustomerStoreHackMap[customer.id].manualRefresh(customer);
    }
};

export interface Props {
    horseForSaleEndpoints?: HorseForSaleEndpoints;
    configurationStore?: ConfigurationStore;
    customerEndpoints?: CustomerEndpoints;
    horseSale: SimpleHorseSale;
    winningBidder: Customer;
    horseForSaleList: Array<HorseForSale>;
    horseForSaleRequests: { [horseForSaleId: number]: EditableHorseForSalePurchase.Fields };
    onForward: () => void;
    onBackward: () => void;
    onCustomerUpdated: (customer: Customer) => any;
}

export interface State {
    wasValidated: boolean;
    showSettlementDialog: boolean;
    currentCustomerStore: CustomerStore | null;
    notPaidPurchase: Array<Customer>;
}

@inject("configurationStore")
@inject("horseForSaleEndpoints")
@inject("customerEndpoints")
@observer
export default class CheckoutInvoiceAndPayment extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            wasValidated: false,
            showSettlementDialog: false,
            currentCustomerStore: null,
            notPaidPurchase: []
        };
    }

    closeSettlementDialog = (updatedCustomer?: Customer | null) => {
        if (updatedCustomer) {
            this.props.onCustomerUpdated(updatedCustomer);
        }
        this.setState({ showSettlementDialog: false, currentCustomerStore: null });
    };

    downloadInvoice = async (invoiceIds: Array<number>) => {
        try {
            const blob = (await this.props.customerEndpoints!.downloadInvoice(invoiceIds)) as Blob;
            invoiceIds.length > 1
                ? fileUtil.ShowBlobOnNewtab(`Invoices_${invoiceIds.join("_")}.pdf`, blob)
                : fileUtil.ShowBlobOnNewtab(`Invoice_${invoiceIds[0]}.pdf`, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    isWinnerBidderAnAgentAuthorizationForThisPurchaser = (purchaser: Customer, horseForSale: HorseForSale): boolean => {
        const winnerBidderId = this.props.winningBidder!.id;
        const currentHorseForSaleSaleId = horseForSale.sale.id;

        return !!(
            purchaser.id !== winnerBidderId &&
            purchaser.delegatorAuthorizations.find(
                aa => aa.agent.id === winnerBidderId && aa.sale.id === currentHorseForSaleSaleId && aa.dateRevoked === null
            )
        );
    };

    get uniquePurchasers(): Array<Customer> {
        return (
            this.props.horseForSaleList
                .map(hfs => hfs.outcome as HammerFallOutcome)
                .map(outcome => outcome.purchasers)
                // Flatten array
                .reduce((a, b) => [...a, ...b], [])
                .map(purchase => purchase.purchaser)
                // Distinct purchasers
                .reduce((a, b) => [...a.filter(p => p.id !== b.id), b], [] as Array<Customer>)
                // Sort it according to the common sorting rules
                .sort(CustomerSortFn(this.props.winningBidder, this.props.horseSale.id))
        );
    }

    findInvoicesForPurchaser = (customerId: number): Array<Invoice> => {
        return (
            this.props.horseForSaleList
                .map(hfs => hfs.outcome as HammerFallOutcome)
                .map(outcome => outcome.purchasers)
                // Flatten array
                .reduce((a, b) => [...a, ...b], [])
                .filter(purchase => purchase.purchaser.id === customerId && purchase.buyerInvoice !== null)
                .map(purchase => purchase.buyerInvoice as Invoice)
        );
    };

    findAllInvoices = (): Array<Invoice> => {
        return (
            this.props.horseForSaleList
                .map(hfs => hfs.outcome as HammerFallOutcome)
                .map(outcome => outcome.purchasers)
                // Flatten array
                .reduce((a, b) => [...a, ...b], [])
                .map(purchase => purchase.buyerInvoice as Invoice)
        );
    };

    findDueDatesForPurchaser = (customerId: number): { [dueDate: string]: number } => {
        return this.findInvoicesForPurchaser(customerId).reduce((a, b) => {
            const date = b.dueDate ? moment(b.dueDate).format("YYYY-MM-DD") : "N/A";
            return { ...a, [date]: (a[date] || 0) + b.balance };
        }, {});
    };

    findHorseForSaleByInvoiceId = (invoiceId: number): HorseForSale | null => {
        return (
            this.props.horseForSaleList.find(hfs =>
                (hfs.outcome as HammerFallOutcome).purchasers.some(p => (p.buyerInvoice && p.buyerInvoice.id === invoiceId) || false)
            ) || null
        );
    };

    printStatement = async (customerId: number, dateFrom: string, dateTo: string) => {
        try {
            const blob = await this.props.customerEndpoints!.printStatement(customerId, dateFrom, dateTo);
            fileUtil.ShowBlobOnNewtab("Statement_of_Account.pdf", blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    componentDidMount() {
        this.uniquePurchasers.forEach(purchaser => {
            // console.log("Setting up customer store for ID: " + purchaser.id);
            SetUpCustomerStore(this.props.customerEndpoints!, purchaser);
        });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        this.uniquePurchasers.forEach(purchaser => {
            // console.log("Setting up customer store for ID: " + purchaser.id);
            SetUpCustomerStore(this.props.customerEndpoints!, purchaser);
        });
    }

    creditOutstanding = (purchaser: Customer): number => {
        const isWinningBidder = this.props.winningBidder.id === purchaser.id;
        return (
            this.props.horseForSaleList
                // If we're a winning bidder, then only include those outcomes with a winning bidder credit application
                .filter(hfs => !isWinningBidder || (hfs.outcome as HammerFallOutcome).winningBidderCreditApplication)
                // Extract and flatten the list of purchasers from all horse for sales
                .map(hfs => (hfs.outcome as HammerFallOutcome).purchasers)
                .reduce((a, b) => [...a, ...b], [])
                // Filter to only those purchasers that match the specified purchaser if a credit application is present,
                // or if the specified purchaser is the winning bidder, then assume that every other purchaser w/o an agent
                // authorization falls under theirs
                .filter(
                    purchase =>
                        (purchase.creditApplication && purchase.purchaser.id === purchaser.id) || (isWinningBidder && purchase.boundBy === null)
                )
                // And then filter all the invoices that are due in the future
                .filter(
                    purchase =>
                        purchase.buyerInvoice &&
                        purchase.buyerInvoice.dueDate &&
                        moment(purchase.buyerInvoice.dueDate).hour(0).minute(0).isAfter(new Date())
                )
                .map(purchase => (purchase.buyerInvoice ? purchase.buyerInvoice.balance : 0))
                .reduce((a, b) => a + b, 0)
        );
    };

    creditGranted = (purchaser: Customer): number => {
        return purchaser.creditApplications
            .filter(
                ca =>
                    (ca.type === "PERMANENT" && ca.state === CreditApplicationState.APPROVED) ||
                    (ca.type === "ON_THE_SPOT" && this.props.horseForSaleList.some(hfs => hfs.id === (ca as OnTheSpotCreditApplication).validFor.id))
            )
            .map((ca: PermanentCreditApplication) => ca.amountRequested)
            .reduce((totalAmount: number, amountRequested: number) => totalAmount + amountRequested, 0);
    };

    isCustomerBalanceGreaterThanAvailableCredit = (p: Customer): boolean => {
        return this.creditGranted(p) < this.creditOutstanding(p);
    };

    // KB 2020-04-17: Note that this used to be named `goToStableRelease` before that step was eliminated
    completeCheckout = async () => {
        const notPaidPurchase = this.uniquePurchasers.filter(p => {
            return (
                this.findInvoicesForPurchaser(p.id)
                    .filter(i => i.dueDate && moment(i.dueDate).isSameOrBefore(new Date(), "date"))
                    .map(i => i.balance)
                    .reduce((acc, i) => acc + i, 0) > 0
            );
        });

        if (notPaidPurchase.length > 0) {
            this.setState({ notPaidPurchase });
        } else {
            this.props.onForward();
        }
    };

    renderPurchaser = (p: Customer, i: Invoice) => {
        if (this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(p, this.findHorseForSaleByInvoiceId(i.id)!)) {
            return <CustomerLinkCheckout page="customer-account" customer={p} />;
        } else if (p.id === this.props.winningBidder.id) {
            return <CustomerLinkCheckout page="customer-account" customer={p} isWinningBider={true} />;
        } else {
            return <CustomerLinkCheckout page="customer-account" customer={p} notHaveAgentAuthorization={true} />;
        }
    };

    render() {
        const openDate = this.props.horseSale.applicationsOpen;
        const { currentFrom, id } = this.props.horseSale;

        const horseSale = this.props.configurationStore!.horseSales.find((hs: SimpleHorseSale) => hs.id === id);
        const cadToUsdExchangeRate = horseSale ? horseSale.cadToUsdExchangeRate : 1;

        const uniquePurchasers = this.uniquePurchasers;
        const overExtended = uniquePurchasers.filter(p => this.isCustomerBalanceGreaterThanAvailableCredit(p));
        const allInvoices = this.findAllInvoices();

        return (
            <div className="card shadow">
                <div className="card-header">Step 4 of 5 - Invoice &amp; Payment</div>
                <div className="card-body">
                    {overExtended.length > 0 && (
                        <div className="form-row">
                            <div className="col-12">
                                <div className="alert alert-warning">
                                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                                    The following customers have over-extended their approved credit:
                                    <br />
                                    <br />
                                    <ul>
                                        {overExtended.map(p => (
                                            <li key={p.id}>
                                                <CustomerLink customer={p} /> (Granted:{" "}
                                                {CovertAndFormatCentsToDollarsWithDollarSign(this.creditGranted(p))}, Used:{" "}
                                                {CovertAndFormatCentsToDollarsWithDollarSign(this.creditOutstanding(p))})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="form-row my-3">
                        <div className="col-12">
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Buyer(s)</th>
                                            <th className="text-center">Horse(s)</th>
                                            <th className="text-center">Invoice #</th>
                                            <th className="text-center">Invoice Amount</th>
                                            <th className="text-center">Due Date</th>
                                            <th className="text-center">Amount Outstanding</th>
                                            <th className="text-center">Amounts(s) Due</th>
                                            <th className="text-center">Account Balance</th>
                                            <th className="text-center">Payment</th>
                                            <th className="text-center">Downloads</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {uniquePurchasers.map(p => {
                                            const isPurchaserWinningBidder = this.props.winningBidder.id === p.id;
                                            const purchaserInvoices = this.findInvoicesForPurchaser(p.id);
                                            const dueDates = this.findDueDatesForPurchaser(p.id);
                                            const invoiceIds = purchaserInvoices.map(i => i.id);

                                            return purchaserInvoices.map((i, index) => {
                                                const isNotAgent = !this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(
                                                    p,
                                                    this.findHorseForSaleByInvoiceId(i.id)!
                                                );
                                                const handleClass =
                                                    "text-center table-row" +
                                                    " " +
                                                    (isPurchaserWinningBidder || isNotAgent
                                                        ? "table-bordered-td-bg" + " " + (isPurchaserWinningBidder ? "text-info" : "")
                                                        : "");
                                                const horserForSaleByInvoice = this.findHorseForSaleByInvoiceId(i.id);
                                                const evenOddClass = index % 2 === 0 ? `` : `row-even`;
                                                return (
                                                    <tr key={i.id} className={index === purchaserInvoices.length - 1 ? `table-row` : ``}>
                                                        {index === 0 && (
                                                            <td
                                                                rowSpan={purchaserInvoices.length}
                                                                style={{ verticalAlign: "middle" }}
                                                                className={handleClass}
                                                            >
                                                                {this.renderPurchaser(p, i)}
                                                            </td>
                                                        )}
                                                        <td className={handleClass + "" + evenOddClass} style={{ verticalAlign: "middle" }}>
                                                            {horserForSaleByInvoice && (
                                                                <Link
                                                                    to={`/horses-for-sale/${horserForSaleByInvoice!.id}/application`}
                                                                    className={`${isPurchaserWinningBidder ? `text-info table-bordered-td-bg` : ``}`}
                                                                    target="_blank"
                                                                >
                                                                    #{horserForSaleByInvoice!.hipNumber}
                                                                </Link>
                                                            )}
                                                        </td>
                                                        <td className={handleClass + "" + evenOddClass} style={{ verticalAlign: "middle" }}>
                                                            {i.id}
                                                            {/* <button
                                                                id={`downloadInvoices_${i.id}`}
                                                                className="btn btn-dark btn-sm ml-2"
                                                                onClick={() => this.downloadInvoice([i.id])}
                                                            >
                                                                <FontAwesomeIcon icon={faDownload} />
                                                            </button>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`downloadInvoices_${i.id}`}
                                                                delay={0}
                                                                className="tooltipstrap"
                                                            >
                                                                Download Invoice
                                                            </UncontrolledTooltip> */}
                                                        </td>
                                                        <td className={handleClass + "" + evenOddClass} style={{ verticalAlign: "middle" }}>
                                                            {CovertAndFormatCentsToDollarsWithDollarSign(i.total)}
                                                        </td>
                                                        <td className={handleClass + "" + evenOddClass} style={{ verticalAlign: "middle" }}>
                                                            {i.dueDate ? moment(i.dueDate).format(`MMM DD, YYYY`) : "N/A"}
                                                        </td>
                                                        <td className={handleClass + "" + evenOddClass} style={{ verticalAlign: "middle" }}>
                                                            {i.balance === 0 && (
                                                                <div className="text-success">
                                                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                                                    Paid
                                                                </div>
                                                            )}
                                                            {i.balance !== 0 &&
                                                                i.dueDate !== null &&
                                                                moment(i.dueDate).isSameOrBefore(new Date(), "day") && (
                                                                    <FontAwesomeIcon icon={faExclamationCircle} className="text-warning mr-2" />
                                                                )}{" "}
                                                            {i.balance !== null && CovertAndFormatCentsToDollarsWithDollarSign(i.balance)}
                                                        </td>
                                                        {index === 0 && (
                                                            <td
                                                                rowSpan={purchaserInvoices.length}
                                                                className={handleClass}
                                                                style={{ verticalAlign: "middle" }}
                                                            >
                                                                <ul
                                                                    style={{
                                                                        paddingLeft: "15px",
                                                                        marginLeft: "0",
                                                                        textAlign: "left",
                                                                        fontSize: "90%",
                                                                        listStyle: "none"
                                                                    }}
                                                                >
                                                                    {Object.keys(dueDates).map(dueDate => (
                                                                        <li key={dueDate}>
                                                                            <strong>
                                                                                {CovertAndFormatCentsToDollarsWithDollarSign(dueDates[dueDate])}
                                                                            </strong>{" "}
                                                                            due {moment(dueDate).format("MMM DD, YYYY")}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </td>
                                                        )}
                                                        {index === 0 && (
                                                            <td
                                                                rowSpan={purchaserInvoices.length}
                                                                className={handleClass}
                                                                style={{ verticalAlign: "middle" }}
                                                            >
                                                                {CovertAndFormatCentsToDollarsWithDollarSign(p.account.balance)}
                                                            </td>
                                                        )}
                                                        {index === 0 && (
                                                            <td
                                                                rowSpan={purchaserInvoices.length}
                                                                className={handleClass}
                                                                style={{ verticalAlign: "middle" }}
                                                            >
                                                                <button
                                                                    className="btn btn-primary btn-sm d-flex"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        this.setState({
                                                                            showSettlementDialog: true,
                                                                            currentCustomerStore: CustomerStoreHackMap[p.id]
                                                                        });
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCashRegister} className="mr-2 mt-1" />
                                                                    <div className="nowrap-white-space">Pay Now</div>
                                                                </button>
                                                            </td>
                                                        )}
                                                        {index === 0 && (
                                                            <td
                                                                rowSpan={purchaserInvoices.length}
                                                                className={handleClass}
                                                                style={{ verticalAlign: "middle" }}
                                                            >
                                                                <button
                                                                    id={`downloadAllInvoices_${p.id}`}
                                                                    className="btn btn-dark btn-sm"
                                                                    onClick={() => this.downloadInvoice(invoiceIds)}
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} />
                                                                </button>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`downloadAllInvoices_${p.id}`}
                                                                    delay={0}
                                                                    className="tooltipstrap"
                                                                >
                                                                    All Invoices
                                                                </UncontrolledTooltip>
                                                                <button
                                                                    id={`downloadStatement_${p.id}`}
                                                                    className="btn btn-dark btn-sm ml-1"
                                                                    onClick={() =>
                                                                        this.printStatement(
                                                                            p.id,
                                                                            moment.utc(currentFrom).format("YYYY-MM-DD"),
                                                                            moment(new Date()).format("YYYY-MM-DD")
                                                                        )
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon icon={faDollarSign} className="mx-1" />
                                                                </button>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`downloadStatement_${p.id}`}
                                                                    delay={0}
                                                                    className="tooltipstrap"
                                                                >
                                                                    Statement of Account
                                                                </UncontrolledTooltip>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            });
                                        })}
                                        {uniquePurchasers.length > 1 && (
                                            <tr>
                                                <th colSpan={3}>TOTALS:</th>
                                                <th className="text-center">
                                                    {CovertAndFormatCentsToDollarsWithDollarSign(
                                                        allInvoices.map(i => i.total).reduce((a, b) => a + b, 0)
                                                    )}
                                                </th>
                                                <th />
                                                <th className="text-center">
                                                    {CovertAndFormatCentsToDollarsWithDollarSign(
                                                        allInvoices.map(i => i.balance).reduce((a, b) => a + b, 0)
                                                    )}
                                                </th>
                                                <th colSpan={3} />
                                                <th className="text-center">
                                                    <button
                                                        id={`downloadAllForEveryone`}
                                                        className="btn btn-dark btn-sm ml-2"
                                                        onClick={() => this.downloadInvoice(allInvoices.map(i => i.id))}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} />
                                                    </button>
                                                    <UncontrolledTooltip
                                                        placement="top"
                                                        target={`downloadAllForEveryone`}
                                                        delay={0}
                                                        className="tooltipstrap"
                                                    >
                                                        Download All Invoices for All Buyers
                                                    </UncontrolledTooltip>
                                                </th>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="for-row text-right">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.props.onBackward();
                            }}
                        >
                            Back to Last Horse
                        </button>
                        <button className="btn btn-primary btn-sm mr-0 ml-3" type="button" onClick={() => this.completeCheckout()}>
                            {/* KB 2020-04-17: Note that this used to be "Go to Stable Release" */}
                            Complete Checkout
                        </button>
                    </div>
                    <SettlementDialog
                        cadToUsdExchangeRate={cadToUsdExchangeRate}
                        customerStore={this.state.currentCustomerStore!}
                        applicationsOpen={openDate}
                        showSettlementDialog={this.state.showSettlementDialog}
                        closeDialog={this.closeSettlementDialog}
                    />
                    <FormDialog
                        title={
                            <>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                Invoice &amp; Payment
                            </>
                        }
                        modalSize="modal-md"
                        show={this.state.notPaidPurchase.length > 0}
                        onClose={() => this.setState({ notPaidPurchase: [] })}
                    >
                        <>
                            <div className="form-row">
                                <div className="col-12">
                                    <div className="text-left mb-3">
                                        A payment for buyers without credit has to be processed as their invoices are due today. The following buyers
                                        haven't been granted credit and are required to pay now. Please, complete the payment or grant them credit and
                                        try again:
                                    </div>
                                    <ul className="pl-3">
                                        {this.state.notPaidPurchase.map(p => (
                                            <li className="text-left" key={p.id}>
                                                {ExtractFriendlyName(p)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="form-row text-right">
                                <div className="col-12 pr-0">
                                    <button type="button" className="btn btn-primary" onClick={() => this.setState({ notPaidPurchase: [] })}>
                                        Complete the Payment or Grant Credit
                                    </button>
                                </div>
                            </div>
                        </>
                    </FormDialog>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutInvoiceAndPayment.tsx